import {
  AbstractType,
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  InjectionToken,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  Type,
} from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  find,
  findIndex,
  forEach,
  isFunction,
  merge,
  pick,
  remove,
} from 'lodash';
import { Observable, Subject } from 'rxjs';

import {
  DndColListConfig,
  DndConfig,
  DndItem,
  FormReadyEvent,
  ItemFormAction,
  ItemFormReadyEvent,
} from '../types';
import { rx } from '@app/shared/rx/rx-wrapper';

import { XuxDndItemComponent } from '../xux-dnd-item/xux-dnd-item.component';
import { DndMode } from '../enums';
import { IFormAction } from '@app/components/form/services/schema-form/schema-form-interfaces';
import { lazyInject } from '@app/containers/base/decorators';
import { XuxDndListService } from '../services/xux-dnd-list.service';
import { DetailDrawerFormData } from '@app/components/popup/types/drawer-form.types';

@Component({
  selector: 'xux-dnd-list',
  templateUrl: './xux-dnd-list.component.html',
  styleUrls: ['./xux-dnd-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxDndListComponent implements OnInit, OnChanges, OnDestroy {
  @lazyInject(XuxDndListService) public xuxDndListService: XuxDndListService;

  @Input()
  public listColConfigs: DndColListConfig[];

  @Input()
  public mode: DndMode = DndMode.IDLE;

  @Input()
  public dndConfig: DndConfig;

  @Input()
  public destroyComponentsWhenDestroy: boolean = true;

  @Output()
  public dragEnd: EventEmitter<DndColListConfig[]> = new EventEmitter();

  @Output()
  public removeItem: EventEmitter<DndItem> = new EventEmitter();

  @Output()
  public itemFormReady: EventEmitter<ItemFormReadyEvent> = new EventEmitter();

  @Output()
  public itemFormAction: EventEmitter<ItemFormAction> = new EventEmitter();

  public isDragModeObs: Observable<boolean>;

  public cancelSubscription$: Subject<void> = new Subject<void>();

  public constructor(
    private appRef: ApplicationRef,
    public componentFactoryResolver: ComponentFactoryResolver,
    public cdf: ChangeDetectorRef,
    public injector: Injector,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngOnInit() {
    this.isDragModeObs = this.getIsDragModeObs();
    this.createComponentInstances();
  }

  public ngOnChanges(changes: Record<keyof XuxDndListComponent, SimpleChange>) {
    if (changes.dndConfig) {
      this.isDragModeObs = this.getIsDragModeObs();
    }

    if (changes.listColConfigs) {
      this.createComponentInstances();
    }
  }

  public ngOnDestroy() {
    if (this.destroyComponentsWhenDestroy) {
      this.xuxDndListService.destroyAllComponent();
    }
  }

  public createComponentInstances() {
    const factory =
      this.componentFactoryResolver.resolveComponentFactory(
        XuxDndItemComponent,
      );

    forEach(this.listColConfigs, (listConfig) => {
      forEach(listConfig.items, (item) => {
        const compMapKey = XuxDndListService.buildCompMapKey(item.key);
        if (
          item.hidden ||
          this.xuxDndListService.componentRefs.has(compMapKey)
        ) {
          return;
        }
        this.createComponent(item, listConfig.key, factory);
      });
    });

    this.cdf.markForCheck();
  }

  public createComponent(
    data: DndItem,
    listKey: string,
    factory: ComponentFactory<XuxDndItemComponent>,
  ) {
    const compMapKey = XuxDndListService.buildCompMapKey(data.key);
    const componentRef = factory.create(this.injector);

    this.appRef.attachView(componentRef.hostView);

    componentRef.instance.itemData = data;
    componentRef.instance.dndConfig = merge(
      {},
      this.dndConfig,
      pick(componentRef.instance.itemData?.content, ['cacheFormValue']),
    );

    // events
    componentRef.instance.formReady
      .pipe(rx.takeUntil(this.cancelSubscription$))
      .subscribe((event: FormReadyEvent) => {
        if (
          componentRef.instance.dndConfig.cacheFormValue &&
          this.xuxDndListService.cacheFormValues.has(compMapKey)
        ) {
          event.form.patchValue(
            this.xuxDndListService.cacheFormValues.get(compMapKey),
          );
        }

        if (!this.xuxDndListService.componentFormRefs.has(compMapKey)) {
          this.xuxDndListService.componentFormRefs.set(compMapKey, event.form);
        }
        this.itemFormReady.emit({ event, itemKey: compMapKey });
      });

    if (componentRef.instance.dndConfig.cacheFormValue) {
      componentRef.instance.formValueChanged
        .pipe(rx.takeUntil(this.cancelSubscription$))
        .subscribe((value: DetailDrawerFormData) => {
          this.xuxDndListService.cacheFormValues.set(compMapKey, value);
        });
    }

    componentRef.instance.formAction
      .pipe(rx.takeUntil(this.cancelSubscription$))
      .subscribe((action: IFormAction) => {
        this.itemFormAction.emit({
          action,
          itemKey: compMapKey,
        });
      });

    componentRef.instance.remove
      .pipe(rx.takeUntil(this.cancelSubscription$))
      .subscribe((item: DndItem) => {
        this.removeColItem(
          item,
          find(
            this.listColConfigs,
            (colConfig) => findIndex(colConfig.items, { key: item.key }) > -1,
          ),
        );
        this.xuxDndListService.destroyComponentByKey(compMapKey);
        this.removeItem.emit(item);

        this.cdf.markForCheck();
      });
    componentRef.changeDetectorRef.detectChanges();

    this.xuxDndListService.componentRefs.set(compMapKey, componentRef);
  }

  /**
   * should destroy the component to avoid memory leak
   *
   * @param componentRef target component reference
   */
  public destroyComponent(componentRef: ComponentRef<any>) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }

  public removeColItem(targetItem: DndItem, colConfig: DndColListConfig) {
    remove(colConfig.items, (item) => item.key === targetItem.key);
  }

  public drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    this.dragEnd.emit(this.listColConfigs);
  }

  public trackByKey(index: number, data: { key: string }) {
    return data?.key;
  }

  public findFirstShowItemIndex(config: DndColListConfig) {
    return findIndex(config.items, (item) => !item.hidden);
  }

  public getItemCompRef(itemKey: string) {
    const compMapKey = XuxDndListService.buildCompMapKey(itemKey);

    return this.xuxDndListService.componentRefs.get(compMapKey);
  }

  public getIsDragModeObs(): Observable<boolean> {
    return this.dndConfig?.modeObs?.pipe(
      rx.map((mode) => mode === DndMode.DRAG),
      rx.distinctUntilChanged(),
    );
  }
}
